<template>
  <div
    class="form-group"
    :class="{
      'form-group--has-error': localError,
      'form-group--has-success': !localError && touched,
    }"
  >
    <label :for="id">{{ label }}</label>
    <div class="signature-pad" :class="{ 'signature-pad--disabled': disabled }">
      <canvas
        ref="canvasRef"
        @mousedown="startDrawing"
        @mousemove="draw"
        @mouseup="stopDrawing"
        @mouseleave="stopDrawing"
        @touchstart="handleTouch"
        @touchmove="handleTouch"
        @touchend="stopDrawing"
      ></canvas>
      <div class="signature-pad__actions">
        <button type="button" @click="clearSignature">{{ clearLabel }}</button>
      </div>
    </div>
    <span v-if="localError" class="form-group__error">{{ localError }}</span>
  </div>
</template>

<script>
  import { ref, watch, onMounted } from 'vue';

  export default {
    props: {
      id: {
        type: String,
        required: true,
      },
      label: {
        type: String,
        required: true,
      },
      clearLabel: {
        type: String,
        required: false,
        default: 'Clear',
      },
      modelValue: {
        type: String,
        required: true,
      },
      error: {
        type: String,
        default: '',
      },
      rules: {
        type: Array,
        default: () => [],
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
      const canvasRef = ref(null);
      const isDrawing = ref(false);
      const localError = ref(props.error);
      const touched = ref(false);
      let ctx;

      onMounted(() => {
        const canvas = canvasRef.value;
        ctx = canvas.getContext('2d');

        // Set canvas size
        canvas.width = canvas.offsetWidth;
        canvas.height = 150;

        // Set drawing style
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';

        // Draw existing signature if modelValue exists
        if (props.modelValue) {
          const img = new Image();
          img.onload = () => {
            ctx.drawImage(img, 0, 0);
          };
          img.src = props.modelValue;
        }
      });

      const startDrawing = (event) => {
        isDrawing.value = true;
        touched.value = true;
        draw(event);
      };

      const draw = (event) => {
        if (!isDrawing.value) return;

        const rect = canvasRef.value.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        ctx.lineTo(x, y);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x, y);
      };

      const handleTouch = (event) => {
        event.preventDefault();
        const touch = event.touches[0];
        const mouseEvent = new MouseEvent('mousemove', {
          clientX: touch.clientX,
          clientY: touch.clientY,
        });

        if (event.type === 'touchstart') {
          startDrawing(mouseEvent);
        } else {
          draw(mouseEvent);
        }
      };

      const stopDrawing = () => {
        isDrawing.value = false;
        ctx.beginPath();
        emit('update:modelValue', canvasRef.value.toDataURL());
      };

      const clearSignature = () => {
        ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height);
        emit('update:modelValue', '');
      };

      watch(
        () => props.error,
        (newError) => {
          localError.value = newError;
        },
        { immediate: true }
      );

      return {
        canvasRef,
        localError,
        touched,
        startDrawing,
        draw,
        stopDrawing,
        clearSignature,
        handleTouch,
      };
    },
  };
</script>

<style lang="scss" scoped>
  .signature-pad {
    border: 1px solid var(--slate-200);
    border-radius: 4px;
    margin-top: 0.5rem;
    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }

    canvas {
      width: 100%;
      border-bottom: 1px solid var(--slate-200);
      cursor: crosshair;
    }

    &__actions {
      padding: 0.5rem;
      text-align: right;

      button {
        padding: 0.25rem 0.75rem;
        font-size: 0.875rem;
        background-color: var(--slate-100);
        border: 1px solid var(--slate-200);
        border-radius: 4px;
        cursor: pointer;

        &:hover {
          background-color: var(--slate-200);
        }
      }
    }
  }
</style>
